'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SeasonalPeriod, getActiveSeasonalPeriod, getDailySeed } from '@/lib/seasonal-movies';

interface ThemeContextType {
  activeTheme: SeasonalPeriod | null;
  loading: boolean;
  refreshTheme: () => Promise<void>;
  colorIndex: number;
  getNextColor: () => string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [activeTheme, setActiveTheme] = useState<SeasonalPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [colorIndex, setColorIndex] = useState(0);

  const fetchActiveTheme = async () => {
    setLoading(true);
    try {
      const theme = await getActiveSeasonalPeriod();
      setActiveTheme(theme);

      // Apply theme to document
      if (theme) {
        applyThemeToDocument(theme);
      } else {
        resetThemeToDefault();
      }
    } catch (error) {
      resetThemeToDefault();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveTheme();
  }, []);

  const applyThemeToDocument = (theme: SeasonalPeriod) => {
    const root = document.documentElement;

    // Remove existing theme style if any
    const existingStyle = document.getElementById('seasonal-theme-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Apply colors based on color mode
    if (theme.colors && theme.colors.length > 0) {
      const colors = theme.colors;
      const oklchColor = hexToOklch(colors[0]);

      // Set CSS variables
      root.style.setProperty('--primary', oklchColor);
      root.style.setProperty('--ring', oklchColor);
      root.style.setProperty('--destructive', oklchColor);

      // Create dynamic CSS rules
      const styleEl = document.createElement('style');
      styleEl.id = 'seasonal-theme-styles';

      let css = '';

      if (theme.colorMode === 'solid') {
        // Replace red with seasonal color, but preserve opacity and text colors
        css = `
          /* Red backgrounds - preserve text color (usually white) */
          .bg-red-600, .bg-red-700, .bg-red-500, .bg-red-400,
          button.bg-red-600, button.bg-red-700 {
            background-color: ${colors[0]} !important;
          }

          /* Red backgrounds with opacity */
          .bg-red-600\\/10 { background-color: ${addAlpha(colors[0], 0.1)} !important; }
          .bg-red-600\\/20 { background-color: ${addAlpha(colors[0], 0.2)} !important; }
          .bg-red-600\\/30 { background-color: ${addAlpha(colors[0], 0.3)} !important; }
          .bg-red-600\\/90 { background-color: ${addAlpha(colors[0], 0.9)} !important; }
          .bg-red-500\\/10 { background-color: ${addAlpha(colors[0], 0.1)} !important; }
          .bg-red-500\\/20 { background-color: ${addAlpha(colors[0], 0.2)} !important; }

          /* Hover states - darken the seasonal color */
          .hover\\:bg-red-700:hover,
          .hover\\:bg-red-600:hover,
          button.bg-red-600:hover,
          button.bg-red-700:hover {
            background-color: ${adjustColor(colors[0], -20)} !important;
          }

          /* Hover states with opacity */
          .hover\\:bg-red-600\\/10:hover { background-color: ${addAlpha(colors[0], 0.1)} !important; }

          /* Red TEXT colors - only change if it's actually visible red text */
          .text-red-600, .text-red-500, .text-red-400 {
            color: ${colors[0]} !important;
          }

          /* Hover red text - only on hover, not always */
          .hover\\:text-red-500:hover,
          .hover\\:text-red-400:hover,
          .hover\\:text-red-300:hover {
            color: ${colors[0]} !important;
          }

          /* Group hover red text - for media cards and person cards */
          .group:hover .group-hover\\:text-red-500,
          .group:hover .group-hover\\:text-red-400,
          .group:hover .group-hover\\:text-red-300 {
            color: ${colors[0]} !important;
          }

          /* Red borders */
          .border-red-600, .border-red-500,
          .ring-red-600 {
            border-color: ${colors[0]} !important;
          }

          /* Border with opacity */
          .border-red-600\\/30 { border-color: ${addAlpha(colors[0], 0.3)} !important; }

          /* Ring colors */
          .ring-red-600 {
            --tw-ring-color: ${colors[0]} !important;
          }

          /* Profile gradient in header dropdown */
          .bg-gradient-to-r.from-red-600.to-red-700 {
            background: linear-gradient(to right, ${colors[0]}, ${adjustColor(colors[0], -20)}) !important;
          }

          /* Profile avatar gradient (from-red-500 to-red-700) */
          .bg-gradient-to-br.from-red-500.to-red-700 {
            background: linear-gradient(to bottom right, ${colors[0]}, ${adjustColor(colors[0], -20)}) !important;
          }

          /* Profile header gradient inside dropdown */
          .bg-gradient-to-br.from-red-950\\/30.to-transparent {
            background: linear-gradient(to bottom right, ${addAlpha(adjustColor(colors[0], -50), 0.3)}, transparent) !important;
          }

          /* Profile ring color */
          .ring-red-500\\/20 {
            --tw-ring-color: ${addAlpha(colors[0], 0.2)} !important;
          }

          /* Gradient bars on detail pages (Movie Information, Genres, Production, etc.) */
          .bg-gradient-to-b.from-red-600.to-red-800 {
            background: linear-gradient(to bottom, ${colors[0]}, ${adjustColor(colors[0], -20)}) !important;
          }

          /* Genre badge gradients - background and hover states */
          .bg-gradient-to-r.from-red-600\\/30.to-red-700\\/20 {
            background: linear-gradient(to right, ${addAlpha(colors[0], 0.3)}, ${addAlpha(adjustColor(colors[0], -10), 0.2)}) !important;
          }
          .hover\\:from-red-600\\/40:hover {
            background: linear-gradient(to right, ${addAlpha(colors[0], 0.4)}, ${addAlpha(adjustColor(colors[0], -10), 0.3)}) !important;
          }

          /* Genre badge text color */
          .text-red-300 {
            color: ${adjustColor(colors[0], 30)} !important;
          }

          /* Header dropdown gradients */
          .bg-gradient-to-br.from-red-600\\/20.via-red-900\\/10.to-transparent {
            background: linear-gradient(to bottom right, ${addAlpha(colors[0], 0.2)}, ${addAlpha(adjustColor(colors[0], -30), 0.1)}, transparent) !important;
          }

          /* Notification unread item gradients */
          .bg-gradient-to-r.from-red-600\\/20.to-red-900\\/10 {
            background: linear-gradient(to right, ${addAlpha(colors[0], 0.2)}, ${addAlpha(adjustColor(colors[0], -30), 0.1)}) !important;
          }

          /* Dashboard progress bar gradients */
          .bg-gradient-to-r.from-red-600.to-red-500 {
            background: linear-gradient(to right, ${colors[0]}, ${adjustColor(colors[0], -10)}) !important;
          }

          /* Browse page category icon background */
          .bg-gradient-to-br.from-red-600\\/20.to-red-800\\/20 {
            background: linear-gradient(to bottom right, ${addAlpha(colors[0], 0.2)}, ${addAlpha(adjustColor(colors[0], -20), 0.2)}) !important;
          }

          /* Browse page category icon border */
          .border-red-500\\/30 {
            border-color: ${addAlpha(colors[0], 0.3)} !important;
          }

          /* SVG fills and strokes that are red */
          svg.fill-red-600, svg.fill-red-500,
          .fill-red-600, .fill-red-500 {
            fill: ${colors[0]} !important;
          }

          svg.text-red-600, svg.text-red-500 {
            color: ${colors[0]} !important;
          }

          /* Scrollbar */
          html::-webkit-scrollbar-thumb,
          body::-webkit-scrollbar-thumb,
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, ${colors[0]}, ${adjustColor(colors[0], -20)}) !important;
          }

          html::-webkit-scrollbar-thumb:hover,
          body::-webkit-scrollbar-thumb:hover,
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, ${adjustColor(colors[0], 20)}, ${colors[0]}) !important;
            box-shadow: 0 0 10px ${addAlpha(colors[0], 0.5)} !important;
          }

          html, body, * {
            scrollbar-color: ${colors[0]} rgba(0, 0, 0, 0.3) !important;
          }

          /* ONLY Exception: Landing page filter tabs - only active tab gets subtle white */
          [data-state="active"][role="tab"] {
            background-color: rgba(255, 255, 255, 0.1) !important;
            color: white !important;
          }

          /* Exception: Cancelled status badge - always stays red */
          .status-cancelled {
            background-color: rgb(220, 38, 38) !important;
          }
        `;
      } else if (theme.colorMode === 'gradient' && colors.length >= 2) {
        const gradient = colors.join(', ');
        css = `
          /* Red backgrounds - preserve text color (usually white) */
          .bg-red-600, .bg-red-700, .bg-red-500, .bg-red-400,
          button.bg-red-600, button.bg-red-700 {
            background: linear-gradient(135deg, ${gradient}) !important;
          }

          /* Red backgrounds with opacity */
          .bg-red-600\\/10 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.1)).join(', ')}) !important; }
          .bg-red-600\\/20 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.2)).join(', ')}) !important; }
          .bg-red-600\\/30 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.3)).join(', ')}) !important; }
          .bg-red-600\\/90 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.9)).join(', ')}) !important; }
          .bg-red-500\\/10 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.1)).join(', ')}) !important; }
          .bg-red-500\\/20 { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.2)).join(', ')}) !important; }

          /* Hover states */
          .hover\\:bg-red-700:hover,
          .hover\\:bg-red-600:hover,
          button.bg-red-600:hover,
          button.bg-red-700:hover {
            background: linear-gradient(135deg, ${colors.map(c => adjustColor(c, -20)).join(', ')}) !important;
          }

          /* Hover states with opacity */
          .hover\\:bg-red-600\\/10:hover { background: linear-gradient(135deg, ${colors.map(c => addAlpha(c, 0.1)).join(', ')}) !important; }

          /* Red TEXT colors - use first color for readability */
          .text-red-600, .text-red-500, .text-red-400 {
            color: ${colors[0]} !important;
          }

          /* Hover red text */
          .hover\\:text-red-500:hover,
          .hover\\:text-red-400:hover,
          .hover\\:text-red-300:hover {
            color: ${colors[0]} !important;
          }

          /* Group hover red text */
          .group:hover .group-hover\\:text-red-500,
          .group:hover .group-hover\\:text-red-400,
          .group:hover .group-hover\\:text-red-300 {
            color: ${colors[0]} !important;
          }

          /* Red borders */
          .border-red-600, .border-red-500 {
            border-color: ${colors[0]} !important;
          }

          /* Border with opacity */
          .border-red-600\\/30 { border-color: ${addAlpha(colors[0], 0.3)} !important; }

          /* Ring colors */
          .ring-red-600 {
            --tw-ring-color: ${colors[0]} !important;
          }

          /* Profile gradient in header dropdown */
          .bg-gradient-to-r.from-red-600.to-red-700 {
            background: linear-gradient(to right, ${gradient}) !important;
          }

          /* Profile avatar gradient */
          .bg-gradient-to-br.from-red-500.to-red-700 {
            background: linear-gradient(to bottom right, ${gradient}) !important;
          }

          /* Profile header gradient inside dropdown */
          .bg-gradient-to-br.from-red-950\\/30.to-transparent {
            background: linear-gradient(to bottom right, ${addAlpha(adjustColor(colors[0], -50), 0.3)}, transparent) !important;
          }

          /* Profile ring color */
          .ring-red-500\\/20 {
            --tw-ring-color: ${addAlpha(colors[0], 0.2)} !important;
          }

          /* Gradient bars on detail pages */
          .bg-gradient-to-b.from-red-600.to-red-800 {
            background: linear-gradient(to bottom, ${gradient}) !important;
          }

          /* Genre badge gradients */
          .bg-gradient-to-r.from-red-600\\/30.to-red-700\\/20 {
            background: linear-gradient(to right, ${colors.map(c => addAlpha(c, 0.25)).join(', ')}) !important;
            border-color: ${addAlpha(colors[0], 0.3)} !important;
          }
          .hover\\:from-red-600\\/40:hover {
            background: linear-gradient(to right, ${colors.map(c => addAlpha(c, 0.35)).join(', ')}) !important;
          }

          /* Genre badge text color - use first color for readability */
          .text-red-300 {
            color: ${adjustColor(colors[0], 30)} !important;
          }

          /* Header dropdown gradients */
          .bg-gradient-to-br.from-red-600\\/20.via-red-900\\/10.to-transparent {
            background: linear-gradient(to bottom right, ${colors.map(c => addAlpha(c, 0.15)).join(', ')}, transparent) !important;
          }

          /* Notification unread item gradients */
          .bg-gradient-to-r.from-red-600\\/20.to-red-900\\/10 {
            background: linear-gradient(to right, ${colors.map(c => addAlpha(c, 0.15)).join(', ')}) !important;
          }

          /* Dashboard progress bar gradients */
          .bg-gradient-to-r.from-red-600.to-red-500 {
            background: linear-gradient(to right, ${gradient}) !important;
          }

          /* Browse page category icon background */
          .bg-gradient-to-br.from-red-600\\/20.to-red-800\\/20 {
            background: linear-gradient(to bottom right, ${colors.map(c => addAlpha(c, 0.2)).join(', ')}) !important;
          }

          /* Browse page category icon border */
          .border-red-500\\/30 {
            border-color: ${addAlpha(colors[0], 0.3)} !important;
          }

          /* SVG fills and strokes */
          svg.fill-red-600, svg.fill-red-500,
          .fill-red-600, .fill-red-500 {
            fill: ${colors[0]} !important;
          }

          svg.text-red-600, svg.text-red-500 {
            color: ${colors[0]} !important;
          }

          /* Scrollbar - create repeating gradient pattern */
          html::-webkit-scrollbar-thumb,
          body::-webkit-scrollbar-thumb,
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(
              180deg,
              ${colors.map((color, i) => `${color} ${(i / colors.length) * 100}%, ${color} ${((i + 1) / colors.length) * 100}%`).join(', ')}
            ) !important;
          }

          html::-webkit-scrollbar-thumb:hover,
          body::-webkit-scrollbar-thumb:hover,
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(
              180deg,
              ${colors.map((color, i) => `${adjustColor(color, 20)} ${(i / colors.length) * 100}%, ${adjustColor(color, 20)} ${((i + 1) / colors.length) * 100}%`).join(', ')}
            ) !important;
            box-shadow: 0 0 10px ${addAlpha(colors[0], 0.5)} !important;
          }

          html, body, * {
            scrollbar-color: ${colors[0]} rgba(0, 0, 0, 0.3) !important;
          }

          /* ONLY Exception: Landing page filter tabs - only active tab gets subtle white */
          [data-state="active"][role="tab"] {
            background-color: rgba(255, 255, 255, 0.1) !important;
            color: white !important;
          }

          /* Exception: Cancelled status badge - always stays red */
          .status-cancelled {
            background-color: rgb(220, 38, 38) !important;
          }
        `;
      } else if (theme.colorMode === 'alternating') {
        // For alternating mode, use first color for all UI elements
        const primaryColor = colors[0];
        css = `
          /* Red backgrounds - preserve text color (usually white) */
          .bg-red-600, .bg-red-700, .bg-red-500, .bg-red-400,
          button.bg-red-600, button.bg-red-700 {
            background-color: ${primaryColor} !important;
          }

          /* Red backgrounds with opacity */
          .bg-red-600\\/10 { background-color: ${addAlpha(primaryColor, 0.1)} !important; }
          .bg-red-600\\/20 { background-color: ${addAlpha(primaryColor, 0.2)} !important; }
          .bg-red-600\\/30 { background-color: ${addAlpha(primaryColor, 0.3)} !important; }
          .bg-red-600\\/90 { background-color: ${addAlpha(primaryColor, 0.9)} !important; }
          .bg-red-500\\/10 { background-color: ${addAlpha(primaryColor, 0.1)} !important; }
          .bg-red-500\\/20 { background-color: ${addAlpha(primaryColor, 0.2)} !important; }

          /* Hover states */
          .hover\\:bg-red-700:hover,
          .hover\\:bg-red-600:hover,
          button.bg-red-600:hover,
          button.bg-red-700:hover {
            background-color: ${adjustColor(primaryColor, -20)} !important;
          }

          /* Hover states with opacity */
          .hover\\:bg-red-600\\/10:hover { background-color: ${addAlpha(primaryColor, 0.1)} !important; }

          /* Red TEXT colors */
          .text-red-600, .text-red-500, .text-red-400 {
            color: ${primaryColor} !important;
          }

          /* Hover red text */
          .hover\\:text-red-500:hover,
          .hover\\:text-red-400:hover,
          .hover\\:text-red-300:hover {
            color: ${primaryColor} !important;
          }

          /* Group hover red text */
          .group:hover .group-hover\\:text-red-500,
          .group:hover .group-hover\\:text-red-400,
          .group:hover .group-hover\\:text-red-300 {
            color: ${primaryColor} !important;
          }

          /* Red borders */
          .border-red-600, .border-red-500 {
            border-color: ${primaryColor} !important;
          }

          /* Border with opacity */
          .border-red-600\\/30 { border-color: ${addAlpha(primaryColor, 0.3)} !important; }

          /* Ring colors */
          .ring-red-600 {
            --tw-ring-color: ${primaryColor} !important;
          }

          /* Profile gradient in header dropdown */
          .bg-gradient-to-r.from-red-600.to-red-700 {
            background: linear-gradient(to right, ${primaryColor}, ${adjustColor(primaryColor, -20)}) !important;
          }

          /* Profile avatar gradient */
          .bg-gradient-to-br.from-red-500.to-red-700 {
            background: linear-gradient(to bottom right, ${primaryColor}, ${adjustColor(primaryColor, -20)}) !important;
          }

          /* Profile header gradient inside dropdown */
          .bg-gradient-to-br.from-red-950\\/30.to-transparent {
            background: linear-gradient(to bottom right, ${addAlpha(adjustColor(primaryColor, -50), 0.3)}, transparent) !important;
          }

          /* Profile ring color */
          .ring-red-500\\/20 {
            --tw-ring-color: ${addAlpha(primaryColor, 0.2)} !important;
          }

          /* Gradient bars on detail pages */
          .bg-gradient-to-b.from-red-600.to-red-800 {
            background: linear-gradient(to bottom, ${primaryColor}, ${adjustColor(primaryColor, -20)}) !important;
          }

          /* Genre badge gradients */
          .bg-gradient-to-r.from-red-600\\/30.to-red-700\\/20 {
            background: linear-gradient(to right, ${addAlpha(primaryColor, 0.3)}, ${addAlpha(adjustColor(primaryColor, -10), 0.2)}) !important;
          }
          .hover\\:from-red-600\\/40:hover {
            background: linear-gradient(to right, ${addAlpha(primaryColor, 0.4)}, ${addAlpha(adjustColor(primaryColor, -10), 0.3)}) !important;
          }

          /* Genre badge text color */
          .text-red-300 {
            color: ${adjustColor(primaryColor, 30)} !important;
          }

          /* Header dropdown gradients */
          .bg-gradient-to-br.from-red-600\\/20.via-red-900\\/10.to-transparent {
            background: linear-gradient(to bottom right, ${addAlpha(primaryColor, 0.2)}, ${addAlpha(adjustColor(primaryColor, -30), 0.1)}, transparent) !important;
          }

          /* Notification unread item gradients */
          .bg-gradient-to-r.from-red-600\\/20.to-red-900\\/10 {
            background: linear-gradient(to right, ${addAlpha(primaryColor, 0.2)}, ${addAlpha(adjustColor(primaryColor, -30), 0.1)}) !important;
          }

          /* Dashboard progress bar gradients */
          .bg-gradient-to-r.from-red-600.to-red-500 {
            background: linear-gradient(to right, ${primaryColor}, ${adjustColor(primaryColor, -10)}) !important;
          }

          /* Browse page category icon background */
          .bg-gradient-to-br.from-red-600\\/20.to-red-800\\/20 {
            background: linear-gradient(to bottom right, ${addAlpha(primaryColor, 0.2)}, ${addAlpha(adjustColor(primaryColor, -20), 0.2)}) !important;
          }

          /* Browse page category icon border */
          .border-red-500\\/30 {
            border-color: ${addAlpha(primaryColor, 0.3)} !important;
          }

          /* SVG fills and strokes */
          svg.fill-red-600, svg.fill-red-500,
          .fill-red-600, .fill-red-500 {
            fill: ${primaryColor} !important;
          }

          svg.text-red-600, svg.text-red-500 {
            color: ${primaryColor} !important;
          }

          /* Scrollbar */
          html::-webkit-scrollbar-thumb,
          body::-webkit-scrollbar-thumb,
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, ${primaryColor}, ${adjustColor(primaryColor, -20)}) !important;
          }

          html::-webkit-scrollbar-thumb:hover,
          body::-webkit-scrollbar-thumb:hover,
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, ${adjustColor(primaryColor, 20)}, ${primaryColor}) !important;
            box-shadow: 0 0 10px ${addAlpha(primaryColor, 0.5)} !important;
          }

          html, body, * {
            scrollbar-color: ${primaryColor} rgba(0, 0, 0, 0.3) !important;
          }

          /* ONLY Exception: Landing page filter tabs - only active tab gets subtle white */
          [data-state="active"][role="tab"] {
            background-color: rgba(255, 255, 255, 0.1) !important;
            color: white !important;
          }

          /* Exception: Cancelled status badge - always stays red */
          .status-cancelled {
            background-color: rgb(220, 38, 38) !important;
          }
        `;
      }

      styleEl.textContent = css;
      document.head.appendChild(styleEl);
    }
  };

  const resetThemeToDefault = () => {
    const root = document.documentElement;

    // Reset to default red theme
    const defaultColor = 'oklch(0.577 0.245 27.325)';
    root.style.setProperty('--primary', defaultColor);
    root.style.setProperty('--ring', defaultColor);
    root.style.setProperty('--destructive', defaultColor);

    // Remove theme styles
    const existingStyle = document.getElementById('seasonal-theme-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
  };

  // Helper to adjust color brightness
  const adjustColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + percent));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));
    return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
  };

  // Helper to add alpha to hex color
  const addAlpha = (hex: string, alpha: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = num >> 16;
    const g = (num >> 8) & 0x00FF;
    const b = num & 0x0000FF;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Helper to convert hex to oklch (simplified - you may want a more robust conversion)
  const hexToOklch = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    // Simple luminance calculation
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Chroma estimation (simplified)
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const chroma = (max - min) * 0.3;

    // Hue calculation (simplified)
    let hue = 0;
    if (chroma !== 0) {
      if (max === r) {
        hue = ((g - b) / chroma) * 60;
      } else if (max === g) {
        hue = (2 + (b - r) / chroma) * 60;
      } else {
        hue = (4 + (r - g) / chroma) * 60;
      }
    }
    if (hue < 0) hue += 360;

    return `oklch(${luminance.toFixed(3)} ${chroma.toFixed(3)} ${hue.toFixed(3)})`;
  };

  const getNextColor = (): string => {
    if (!activeTheme || !activeTheme.colors || activeTheme.colors.length === 0) {
      return '#dc2626'; // Default red
    }

    if (activeTheme.colorMode === 'alternating') {
      const colors = activeTheme.colors;
      const color = colors[colorIndex % colors.length];
      setColorIndex(prev => prev + 1);
      return color;
    }

    // For solid or gradient, always return first color
    return activeTheme.colors[0];
  };

  return (
    <ThemeContext.Provider
      value={{
        activeTheme,
        loading,
        refreshTheme: fetchActiveTheme,
        colorIndex,
        getNextColor,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
